﻿import { motionCount } from '../../../Foundation/motion';

export class Calculator {
  /**
   * Creates a new instance of a Calculator component
   * @param {HTMLElement} element Calculator DOM element
   */
  constructor(element) {
    this.el = element;
    this._refs = {
      totalInput: this.el.querySelector('.calculator__total'),
      calculateButton: this.el.querySelector('.calculator__button'),
      resultSection: this.el.querySelector('.section__result'),
      factorsListSection: this.el.querySelector('.section__factors-list'),
      financialGainsText: this.el.querySelector('#financial-gains-text'),
      financialGainsValue: this.el.querySelector('#financial-gains-value'),
    };
    this._consts = {
      member: 'MEMBER',
      silver: 'SILVER',
      gold: 'GOLD',
      platinum: 'PLATINUM',
      diamond: 'DIAMOND',
    };
    this._props = {
      estimatedStatus: undefined,
      numberFormatter: undefined,
    };
    this.config = this.parseConfiguration();

    if (!this.config) return;
    this.init();

    // Events
    this._refs.totalInput.addEventListener('keypress', (event) => this.onTotalKeypress(event));
    this._refs.calculateButton.addEventListener('click', () => this.onCalculateClick());
  }

  parseConfiguration() {
    try {
      return JSON.parse(this.el.dataset.calculatorConfig);
    } catch (e) {
      console.error('Calculator: configuration to parse is not valid JSON!', e);
    }
  }

  init() {
    this.setInputPlaceholder();
    this._props.numberFormatter = this.createNumberFormatter();
  }

  setInputPlaceholder() {
    const { currencySymbol, defaultValue } = this.config;
    this._refs.totalInput.placeholder = `${currencySymbol}${defaultValue}`;
  }

  createNumberFormatter() {
    const { cultureInfoName } = this.config;
    return new Intl.NumberFormat(cultureInfoName, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  onTotalKeypress(event) {
    const charCode = event.which ? event.which : event.keyCode;

    if (!this.isCharCodeValid(charCode)) {
      // Prevent all characters not 0-9 from being entered in the input
      event.preventDefault();
      return;
    }

    // 13 = enter
    if (charCode === 13) {
      this.onCalculateClick();
    }
  }

  isCharCodeValid(charCode) {
    // 13 = enter, 48-57 = number
    return charCode === 13 || (charCode >= 48 && charCode <= 57);
  }

  onCalculateClick() {
    const { defaultValue } = this.config;
    const { totalInput } = this._refs;
    const inputValue = totalInput.value;

    if (inputValue.trim() === '') {
      totalInput.value = '';
      this.calculate(defaultValue);
    } else if (!Number.isNaN(+inputValue)) {
      this.calculate(+inputValue);
    }
  }

  calculate(value) {
    const status = this.estimateStatus(value);

    if (status) {
      const flexPoints = this.calculateFlexPoints(status, value);
      const flexPointsValue = this.convertFlexPointsToValue(flexPoints);
      const savingPlansValue = this.calculateSavingsPlan(status, value);
      const freeChargerValue = this.calculateFreeCharger(value);
      const freeShippingValue = this.calculateFreeShipping(status, value);
      const accessoriesValue = this.calculateAccessories(status, value);

      const estimatedFinancialGains =
        flexPointsValue +
        savingPlansValue +
        freeChargerValue +
        freeShippingValue +
        accessoriesValue;
      const spanEl = this.renderEstimatedFinancialGains(estimatedFinancialGains);

      this.renderFactorsList(status);
      this.showResultSection();
      motionCount(spanEl);
      this.trackCalculation(value);
    } else {
      this.hideResultSection();
    }
  }

  estimateStatus(value) {
    const { estimatedStatus } = this._props;
    const status = this.getStatus(value);

    this.resetEstimatedStatus(estimatedStatus);
    this.setEstimatedStatus(status);

    return status;
  }

  getStatus(value) {
    const { diamond, platinum, gold, silver, member } = this._consts;
    const { statusLevels } = this.config;

    switch (true) {
      case statusLevels[diamond] !== null && value >= statusLevels[diamond]:
        return diamond;
      case statusLevels[platinum] !== null && value >= statusLevels[platinum]:
        return platinum;
      case statusLevels[gold] !== null && value >= statusLevels[gold]:
        return gold;
      case statusLevels[silver] !== null && value >= statusLevels[silver]:
        return silver;
      case statusLevels[member] !== null && value >= statusLevels[member]:
        return member;
      default:
        return undefined;
    }
  }

  resetEstimatedStatus(prevStatus) {
    const { financialGainsText } = this._refs;
    const { translations } = this.config;

    if (prevStatus) {
      financialGainsText.innerText = financialGainsText.innerText.replace(
        translations[prevStatus],
        '{}',
      );
    }
  }

  setEstimatedStatus(status) {
    const { financialGainsText } = this._refs;
    const { translations } = this.config;
    this._props.estimatedStatus = status;

    if (status) {
      financialGainsText.innerText = financialGainsText.innerText.replace(
        '{}',
        translations[status],
      );
    }
  }

  renderFactorsList(status) {
    const { factorsListSection } = this._refs;
    const { factorsList } = this.config;
    const fastorsBasedOnStatus = factorsList[status];

    factorsListSection.innerHTML = '';

    fastorsBasedOnStatus?.forEach((item) => {
      const liElement = document.createElement('li');
      liElement.className = 'section__factors-list__item';

      const imgElement = document.createElement('img');
      imgElement.className = 'icon';
      imgElement.src = '/assets/images/resound/icon__list-tick.svg';
      imgElement.alt = 'tick';

      const spanElement = document.createElement('span');
      spanElement.className = 'section__text section__text--list';
      spanElement.textContent = item;

      liElement.appendChild(imgElement);
      liElement.appendChild(spanElement);
      factorsListSection.appendChild(liElement);
    });
  }

  calculateFlexPoints(status, value) {
    const { flexPoints } = this.config;
    const pointsEarned = this.convertPercentToMultiplier(flexPoints.pointsEarnedPercent) * value;
    let totalPoints = pointsEarned;

    if (!flexPoints.statusPercent.disabled) {
      const statusMultiplier =
        this.convertPercentToMultiplier(flexPoints.statusPercent[status]) + 1;
      totalPoints = totalPoints * statusMultiplier;
    }

    if (!flexPoints.annualBonusPercent.disabled) {
      const annualBonusMultiplier =
        this.convertPercentToMultiplier(flexPoints.annualBonusPercent[status]) + 1;
      totalPoints = totalPoints * annualBonusMultiplier;
    }

    return totalPoints;
  }

  convertFlexPointsToValue(flexPoints) {
    return flexPoints * this.config.flexPoints.monetaryValue;
  }

  calculateSavingsPlan(status, value) {
    const { savingPlans } = this.config;

    if (savingPlans.disabled) return 0;
    const brandContribution = savingPlans.amountPerUnit[status];
    const unitsOrdered = value / savingPlans.aspForUnit;

    return unitsOrdered * brandContribution;
  }

  calculateFreeCharger(value) {
    const { savingPlans, freeCharger } = this.config;

    if (freeCharger.disabled) return 0;
    const unitsOrdered = value / savingPlans.aspForUnit;
    const practiceBinauralMultiplier = this.convertPercentToMultiplier(
      freeCharger.practiceBinauralPercent,
    );
    const rechargeAbilityMultiplier = this.convertPercentToMultiplier(
      freeCharger.rechargeAbilityPercent,
    );
    const topLevelTechnologyMultiplier = this.convertPercentToMultiplier(
      freeCharger.topLevelTechnologyPercent,
    );
    const basicLevelTechnologyMultiplier = this.convertPercentToMultiplier(
      freeCharger.basicLevelTechnologyPercent,
    );
    const plusLevelTechnologyMultiplier = this.convertPercentToMultiplier(
      freeCharger.plusLevelTechnologyPercent,
    );
    const budgetLevelTechnologyMultiplier = this.convertPercentToMultiplier(
      freeCharger.budgetLevelTechnologyPercent,
    );

    return (
      ((unitsOrdered * practiceBinauralMultiplier) / 2 +
        unitsOrdered * (1 - practiceBinauralMultiplier)) *
        rechargeAbilityMultiplier *
        (topLevelTechnologyMultiplier +
          basicLevelTechnologyMultiplier +
          plusLevelTechnologyMultiplier) *
        freeCharger.premiumChargerPrice +
      ((unitsOrdered * practiceBinauralMultiplier) / 2 +
        unitsOrdered * (1 - practiceBinauralMultiplier)) *
        rechargeAbilityMultiplier *
        budgetLevelTechnologyMultiplier *
        freeCharger.standardChargerPrice
    );
  }

  calculateFreeShipping(status, value) {
    const { savingPlans, freeCharger, shipping } = this.config;
    const hasFreeShipping = shipping.freeShipping[status];

    if (shipping.disabled || !hasFreeShipping) return 0;
    const unitsOrdered = value / savingPlans.aspForUnit;
    const practiceBinauralMultiplier = this.convertPercentToMultiplier(
      freeCharger.practiceBinauralPercent,
    );

    return (
      ((unitsOrdered * practiceBinauralMultiplier) / 2 +
        unitsOrdered * (1 - practiceBinauralMultiplier)) *
      shipping.cost
    );
  }

  calculateAccessories(status, value) {
    const { accessories } = this.config;

    const isConfigValid = this.validateAccessoriesConfig(accessories);

    if (!accessories || accessories?.disabled || !isConfigValid) return 0;

    const { spentOnAccessoriesPercent, statusPercent } = accessories;

    const accessoriesAmount = value * this.convertPercentToMultiplier(spentOnAccessoriesPercent);
    const statusMultiplier = this.convertPercentToMultiplier(statusPercent[status]);

    return accessoriesAmount * statusMultiplier;
  }

  validateAccessoriesConfig(accessories) {
    try {
      const { statusPercent, spentOnAccessoriesPercent } = accessories;

      const isValidPercent = (value) => typeof value === 'number' && value >= 0 && value <= 100;

      const isBasePercentValid = isValidPercent(spentOnAccessoriesPercent);

      const areStatusLevelValid = Object.values(statusPercent).every((value) =>
        isValidPercent(value),
      );

      return isBasePercentValid && areStatusLevelValid;
    } catch {
      return false;
    }
  }

  convertPercentToMultiplier(value) {
    return value / 100;
  }

  renderEstimatedFinancialGains(value) {
    const { currencySymbol } = this.config;
    const { financialGainsValue } = this._refs;
    const { numberFormatter } = this._props;
    const formattedValue = numberFormatter.format(value);
    financialGainsValue.innerHTML = '';
    const spanEl = document.createElement('span');
    spanEl.setAttribute('data-motion-count', true);
    spanEl.setAttribute('data-end', `${currencySymbol}${formattedValue}`);
    financialGainsValue.appendChild(spanEl);

    return spanEl;
  }

  showResultSection() {
    this._refs.resultSection.style.display = 'flex';
  }

  hideResultSection() {
    this._refs.resultSection.style.display = 'none';
  }

  trackCalculation(value) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'calculation', value });
  }
}
